import { Fee, useField } from '@kizi/comparator-core';
import { Anchor, Div, DivProps, P } from '@kizi/core-components';
import { IconV2 } from '@kizi/shared-components';
import React from 'react';
import { DetailsTabProps } from '.';
import { formatPrice } from '../../../../../utils/global';
import { Field } from '../../../../Product';
import { ListComp, ListGroup } from '../../../../Product/ExpandedPart/List';
import { ProductModal } from '../../../../modals/Reimbursements/ProductModal';

export const DetailsTabHome: React.FC<DetailsTabProps> = ({ data }) => {
  const policyDetails = data.homePackage?.policyPolicyDetails ?? [];
  const { fees = [] } = data;
  const providerType = data.provider.type;
  const filters = useField('filters');
  const filtersData = JSON.parse(filters?.value as string);

  function getGroupByFees(list: Field[], title: string): ListGroup[] {
    return list.length ? [{ list, title }] : [];
  }

  function getFilteredCoverages(policyDetails) {
    return policyDetails?.map((policy) => {
      const coverages = {
        'Inboedelverzekering': policy?.coverageHomes,
        'Opstalverzekering': policy?.coverageBuildings,
        'Aansprakelijkheidsverzekering': policy?.coverageLiabilities,
      };
      return Object.entries(coverages).reduce(
        (a, [k, v]) => (v === null ? a : ((a[k] = v), a)),
        {},
      );
    });
  }

  function getLiabilityCoverages(policyDetails) {
    const liabilityCoverages = policyDetails?.map((policy) => {
      const coverages = {
        coverageLiabilities: policy?.coverageLiabilities,
      };

      return Object.entries(coverages).reduce(
        (a, [k, v]) => (v === null ? a : ((a[k] = v), a)),
        {},
      );
    });
    return liabilityCoverages.filter(
      (value) => Object.keys(value).length !== 0,
    );
  }

  function getDeductibleAmounts(coverages) {
    let keys: any[] = [];
    let amounts: any[] = [];

    Object.keys(coverages).forEach((key) => {
      for (const k in coverages[key]) {
        keys.push(...Object.keys(coverages[key]));
        amounts.push(coverages[key][k][0].deductibleAmount);
      }
    });
    return keys.map((key, index) => ({
      label: key,
      value: formatPrice(amounts[index], 0),
      valueIcon: false,
    }));
  }

  const deductibles = getDeductibleAmounts(getFilteredCoverages(policyDetails));

  const additionalFees: Field[] = [];

  if (filtersData?.glass[0] === '0') {
    additionalFees.push(...getFieldByCategory(fees, 'GLASS'));
  }

  if (filtersData?.outsideCoverage[0] !== '-1') {
    additionalFees.push(...getFieldByCategory(fees, 'OUTDOOR'));
  }

  const liability =
    getLiabilityCoverages(policyDetails)[0]?.coverageLiabilities;

  if (liability) {
    const liabilitySumInsured = {
      label: 'Verzekerd bedrag aansprakelijkheid',
      value: formatPrice(liability[0].sumInsured, 0),
    };
    additionalFees.unshift(liabilitySumInsured);
  }

  const filterOutdoorFee = (fees) =>
    fees.filter((f) => f.label !== 'Verzekerd bedrag buitenshuisdekking');

  const _fees =
    filtersData?.outsideCoverage[0] === '-1' ? filterOutdoorFee(fees) : fees;

  const contentFees = getFieldByCategory(_fees, 'CONTENT');

  const {
    insuredAmountCollections = null,
    customProperties = {
      insuredAmountAntiques: null,
      insuredAmountArt: null,
      insuredAmountAudioVideo: null,
    },
    insuredAmountAudioVideoComputerEquipment = null,
    insuredAmountMusicalInstruments = null,
    insuredAmountJewelry = null,
  } = policyDetails?.[0]?.coverageHomes?.[0] || {
    customProperties: {
      insuredAmountAntiques: null,
      insuredAmountArt: null,
      insuredAmountAudioVideo: null,
    },
  };
  const feesRiskProductMapping = [
    {
      label: 'Verzameling',
      value: insuredAmountCollections,
    },
    {
      label: 'Antiek',
      value: customProperties?.insuredAmountAntiques,
    },
    {
      label: 'Spelcomputer',
      value: insuredAmountAudioVideoComputerEquipment,
    },
    {
      label: 'Instrumenten',
      value: insuredAmountMusicalInstruments,
    },
    {
      label: 'Kunst',
      value: customProperties?.insuredAmountArt,
    },
    {
      label: 'Lijfsieraden',
      value: insuredAmountJewelry,
    },
    {
      label: 'Audio-apparatuur',
      value: customProperties?.insuredAmountAudioVideo,
    },
  ];

  const filteredFeesRiskProductMapping = getFeesForRiskProducts(
    feesRiskProductMapping,
  );
  const homeFees = getFieldByCategory(fees, 'HOME');
  const rightSideData = [
    ...getGroupByFees(additionalFees, 'Aanvullende dekkingen'),
    ...getGroupByFees(deductibles, 'Eigen risico'),
    {
      children:
        providerType === 'risk' ? (
          <>
            {data.provider['coverageConditions']?.coverageBuildings?.map(
              (c) => (
                <Document
                  href={c.urlAttachment}
                  label={c.label}
                  key={c.label}
                />
              ),
            )}
            {data.provider['coverageConditions']?.coverageHomes?.map((c) => (
              <Document href={c.urlAttachment} label={c.label} key={c.label} />
            ))}
            {data.provider['coverageConditions']?.coverageLiabilities?.map(
              (c) => (
                <Document
                  href={c.urlAttachment}
                  label={c.label}
                  key={c.label}
                />
              ),
            )}
            {data.provider['documents']?.map((c) => (
              <Document href={c.url} label={c.label} key={c.label} />
            ))}
          </>
        ) : (
          <>
            {data.provider['urls'].household && (
              <>
                <Document
                  href={data.provider['urls'].household.insuranceCardUrl}
                  label={'Verzekeringskaart Inboedelverzekering'}
                />
                <Document
                  href={data.provider['urls'].household.termsAndConditionsUrl}
                  label={'Polisvoorwaarden Inboedelverzekering'}
                  mb={3}
                />
              </>
            )}
            <>
              {data.provider['urls'].building && (
                <>
                  <Document
                    href={data.provider['urls'].building.insuranceCardUrl}
                    label={'Verzekeringskaart Opstalverzekering'}
                  />
                  <Document
                    href={data.provider['urls'].building.termsAndConditionsUrl}
                    label={'Polisvoorwaarden Opstalverzekering'}
                    mb={3}
                  />
                </>
              )}
            </>
            <>
              {data.provider['urls'].liability && (
                <>
                  <Document
                    href={data.provider['urls'].liability.insuranceCardUrl}
                    label={'Verzekeringskaart Aansprakelijkheidsverzekering'}
                  />
                  <Document
                    href={data.provider['urls'].liability.termsAndConditionsUrl}
                    label={'Polisvoorwaarden Aansprakelijkheidsverzekering'}
                    mb={3}
                  />
                </>
              )}
            </>
          </>
        ),
      title: 'Verzekeringskaarten en Polisvoorwaarden',
    },
  ];

  return (
    <>
      <P mt={[3, null, 0]} mb={[5, null, 6]} variant="footnoteRegularLight">
        Schade door brand, diefstal, blikseminslag, neerslag en storm is
        standaard meeverzekerd
      </P>
      <Div display={[null, null, 'flex']} gridGap={5}>
        <ListComp
          groups={[
            ...(contentFees.length > 0
              ? getGroupByFees(contentFees, 'Inboedelverzekering')
              : getGroupByFees(
                  filteredFeesRiskProductMapping,
                  'Inboedelverzekering',
                )),
            ...getGroupByFees(homeFees, 'Opstalverzekering'),
          ]}
        />
        <ListComp groups={rightSideData} />
      </Div>
      {providerType === 'moneyview' && (
        <Div display={'flex'}>
          <ProductModal data={data} buttonText="Toon alle dekkingen" />
        </Div>
      )}
    </>
  );
};

interface DocumentProps extends DivProps {
  href: string;
  label: string;
}

const Document: React.FC<DocumentProps> = ({ href, label, ...restProps }) => {
  return (
    <Div display="flex" {...restProps}>
      <IconV2 fill="main.1" height={20} mr={2} name="document" width={20} />
      <Anchor
        color="main.1"
        href={href}
        my="auto"
        style={{ textDecoration: 'none' }}
        target="_blank"
      >
        {label}
      </Anchor>
    </Div>
  );
};

const labels = {
  '-32767': 'nvt',
  '0': formatPrice(0, 0),
  '1': 'green-check',
  '32767': 'nvt',
  '999999999': 'Onbeperkt',
  ja: 'green-check',
  nee: 'small-cancel',
};

export function getFieldByCategory(
  _fees: Fee[],
  category: Fee['category'],
): Field[] {
  return _fees
    .filter((fee) => fee.category === category)
    .map((fee) => {
      let value = fee.value;

      if (Object.keys(labels).includes(fee.value)) {
        value = labels[fee.value];
      }

      if (Number(value)) {
        value = 'tot ' + formatPrice(fee.value, 0);
      }

      return {
        ...fee,
        value,
        valueIcon: ['green-check', 'small-cancel'].includes(value),
      };
    });
}

export function getFeesForRiskProducts(
  _fees: {
    label: string;
    value: number | null;
  }[],
): Field[] {
  return _fees
    .filter((fee) => fee.value !== null)
    .map((fee) => {
      let value = fee?.value?.toString() ?? '';

      if (Object.keys(labels).includes(value)) {
        value = labels[value];
      }

      if (Number(value)) {
        value = 'tot ' + formatPrice(fee.value, 0);
      }

      return {
        ...fee,
        value,
        valueIcon: ['green-check', 'small-cancel'].includes(value),
      };
    });
}
